import React from 'react';
import { useNavigate } from 'react-router';
import { PageHeader, Card, CardContent, Badge } from '@redbax/ui-kit';
import { cn } from '@redbax/ui-kit';
import { Bot, CheckCircle2, XCircle } from 'lucide-react';
import { useAdminAiServices } from '../api/admin-hooks';
import type { AdminAiService } from '../types/admin-types';

function ServiceCard({ service }: { service: AdminAiService }) {
  const navigate = useNavigate();

  return (
    <Card
      className="cursor-pointer transition-colors hover:border-red-accent/50"
      onClick={() => navigate(`/ai-services/${service.id}`)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-raised">
              <Bot className="h-5 w-5 text-text-secondary" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-text-primary">{service.name}</h3>
              {service.provider && (
                <p className="text-xs text-text-secondary">{service.provider}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{service.category}</Badge>
            {service.isEnabled ? (
              <CheckCircle2 className="h-4 w-4 text-green-ok" />
            ) : (
              <XCircle className="h-4 w-4 text-text-secondary" />
            )}
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3 text-xs text-text-secondary">
          <span>{service.domains.length} domain{service.domains.length !== 1 ? 's' : ''}</span>
          <span>{service.extractionSteps.length} extraction step{service.extractionSteps.length !== 1 ? 's' : ''}</span>
          <span>{service.pathFilters.length} path filter{service.pathFilters.length !== 1 ? 's' : ''}</span>
        </div>
      </CardContent>
    </Card>
  );
}

export function AdminAiServicesPage() {
  const { data: services, isLoading } = useAdminAiServices();

  const grouped = React.useMemo(() => {
    if (!services) return {};
    const groups: Record<string, AdminAiService[]> = {};
    for (const service of services) {
      const cat = service.category || 'Uncategorized';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(service);
    }
    return groups;
  }, [services]);

  if (isLoading) {
    return (
      <div>
        <PageHeader title="AI Services" description="Manage built-in AI service definitions" />
        <div className="space-y-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg bg-surface-raised" />
          ))}
        </div>
      </div>
    );
  }

  const categories = Object.keys(grouped).sort();

  return (
    <div>
      <PageHeader title="AI Services" description="Manage built-in AI service definitions" />

      {categories.length === 0 && (
        <div className="flex h-32 items-center justify-center text-sm text-text-secondary">
          No AI services found
        </div>
      )}

      {categories.map((category) => (
        <div key={category} className="mb-8">
          <h2 className="mb-3 text-lg font-semibold text-text-primary">{category}</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {grouped[category].map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
